import { useEffect, useRef, useState } from "react";
import {
  EventDateFilterItem,
  EventDateFilterWrapper,
  EventScheduleDateFilterWrapper,
  EventScheduleDateHeader,
  EventScheduleFilterWrapper,
} from "./style";
import { getTodayDate } from "../../../../../utils/date";
import { EventScheduleCountResponse } from "../../../../../api/event-schedule";
import EventScheduleFilter, { FilterDto } from "../filter";
import {
  fornatDateBydayjs,
  formatLabelDate,
  getDateFromDayNumber,
  isDaySelected,
} from "../../utils/calender";
import { DaysDto } from "../../hook/useSchedule";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);

const KST = "Asia/Seoul";

export interface EventScheduleDateFilterProps {
  date: string;
  renderDays: (currentMonthLabel: string) => DaysDto;
  setDate: (value: string) => void;
  setYear: (value: number) => void;
  setMonth: (value: number) => void;
  setSelectedAttendee: (value: FilterDto) => void;
  setSelectedPub: (value: FilterDto) => void;
  selectedAttendee: FilterDto;
  selectedPub: FilterDto;
  attendeeList: FilterDto[];
  pubList: FilterDto[];
  countList: EventScheduleCountResponse[];
}

const EventScheduleDateFilter = ({
  date,
  setDate,
  renderDays,
  selectedAttendee,
  selectedPub,
  setSelectedAttendee,
  setSelectedPub,
  setYear,
  setMonth,
  countList,
  attendeeList,
  pubList,
}: EventScheduleDateFilterProps) => {
  const [days, setDays] = useState<
    { day: number; week: string; count: number }[]
  >([]);
  const [currentMonthLabel, setCurrentMonthLabel] = useState<string>(
    date ? date : getTodayDate()
  );
  const [attendeeOpen, setAttendeeOpen] = useState<boolean>(false);
  const [pubOpen, setPubOpen] = useState<boolean>(false);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [burlIndex, setBurlIndex] = useState<number>(-1);

  const changeMonth = (offset: number) => {
    const current = dayjs
      .tz(currentMonthLabel, "YYYY-MM-DD", KST)
      .add(offset, "month");

    const today = dayjs.tz(getTodayDate(), "YYYY-MM-DD", KST);
    let newCurrent: dayjs.Dayjs;

    if (current.month() + 1 !== today.month() + 1) {
      // 해당 월의 1일
      newCurrent = current.date(1);
    } else {
      // 오늘 날짜의 일자로 설정
      newCurrent = current.date(today.date());
    }

    const newLabel = newCurrent.format("YYYY-MM-DD");
    const [year, month] = newLabel.split("-");

    if (+year <= 2025 && +month <= 6) return;
    setCurrentMonthLabel(newLabel);
    setYear(+year);
    setMonth(+month);
    setDate("");
  };

  useEffect(() => {
    if (date) {
      setCurrentMonthLabel(date);
    }
  }, [date]);

  useEffect(() => {
    const days = renderDays(currentMonthLabel);
    setDays(days.dayList);
  }, [currentMonthLabel, countList]);

  const handleDate = (day: number) => {
    const newDate = getDateFromDayNumber(currentMonthLabel, day);
    setDate(newDate);
  };

  useEffect(() => {
    let currentDate = date;

    if (!date) {
      currentDate = currentMonthLabel;
    }

    const day = +currentDate.split("-")[2];
    const index = days.findIndex((d) => d.day === day);
    // 저장한 날짜 중 선택 된 날짜 찾기
    const el = itemRefs.current[index];
    if (el) {
      // 선택된 날짜의 엘리먼트가 중심에 오도록 스크롤
      el.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  }, [date, days]);

  useEffect(() => {
    setBurlIndex(-1);
  }, [date]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const intersectingMap = new Map<number, number>();

        entries.forEach((entry) => {
          const index = Number(entry.target.getAttribute("data-index"));
          const ratio = entry.intersectionRatio;
          if (ratio < 0.9) {
            intersectingMap.set(index, ratio);
          }
        });

        if (intersectingMap.size > 0 && intersectingMap.size <= 2) {
          // 가장 작은 index 추출
          const minIndex = Math.min(...Array.from(intersectingMap.keys()));

          if (minIndex === 0) return;
          if (burlIndex === -1) {
            setBurlIndex(minIndex);
            return;
          }
          if (minIndex > burlIndex) return;
          setBurlIndex(minIndex);
        } else {
          // 다 보이면 모두 false
          setBurlIndex(-1);
        }
      },
      {
        threshold: Array.from({ length: 20 }, (_, i) => i / 20),
      }
    );

    itemRefs.current.forEach((el, i) => {
      if (el) {
        el.setAttribute("data-index", i.toString());
        observer.observe(el);
      }
    });
    return () => {
      observer.disconnect();
    };
  }, [itemRefs, days, date]);

  const isToday = (day: number) => {
    const [curYear, curMonth] = currentMonthLabel.split("-");
    const [todayYear, todayMonth, todayDay] = getTodayDate().split("-");
    return (
      curMonth === todayMonth && curYear === todayYear && +todayDay === day
    );
  };

  return (
    <EventScheduleDateFilterWrapper>
      <EventScheduleDateHeader>
        <div className="date">
          <img
            src="/image-web/event/calender-left.svg"
            alt="left"
            onClick={() => changeMonth(-1)}
          />
          <div onClick={() => setDate("")}>
            {formatLabelDate(currentMonthLabel)}
          </div>
          <img
            src="/image-web/event/calender-right.svg"
            alt="right"
            onClick={() => changeMonth(1)}
          />
        </div>
        <EventScheduleFilterWrapper>
          <div className="filter-wrapper">
            <EventScheduleFilter
              open={attendeeOpen}
              setOpen={setAttendeeOpen}
              setOtherOpen={setPubOpen}
              filterList={attendeeList}
              label={"크리에이터"}
              selectedItem={selectedAttendee}
              setSelectedItem={setSelectedAttendee}
              setDate={setDate}
            />
            <EventScheduleFilter
              open={pubOpen}
              setOpen={setPubOpen}
              setOtherOpen={setAttendeeOpen}
              filterList={pubList}
              label={"매장"}
              selectedItem={selectedPub}
              setSelectedItem={setSelectedPub}
              setDate={setDate}
            />
          </div>
        </EventScheduleFilterWrapper>
      </EventScheduleDateHeader>
      <EventDateFilterWrapper>
        {days.map((item, i) => (
          <EventDateFilterItem
            key={i}
            ref={(el) => (itemRefs.current[i] = el)} // 각각의 날짜 저장
            onClick={() => handleDate(item.day)}
            isSelected={isDaySelected(currentMonthLabel, item.day, date)}
            isBurl={burlIndex === i}
            isToday={isToday(item.day)}
          >
            <div className="count-wrapper">
              {Array.from({ length: item.count }, (_, i) => (
                <div className="count" key={i} />
              ))}
            </div>
            <div className="week">{item.week}</div>
            <div className="day">{item.day}</div>
          </EventDateFilterItem>
        ))}
      </EventDateFilterWrapper>
    </EventScheduleDateFilterWrapper>
  );
};

export default EventScheduleDateFilter;
